import { ActionIcon, Badge, Group, Text, Tooltip } from "@mantine/core";
import {
	IconArrowsMaximize,
	IconArrowsMinimize,
	IconMenu2,
	IconMessagePlus,
	IconX,
} from "@tabler/icons-react";
import { useSnapshot } from "valtio";
import {
	assistantStore,
	setAssistantView,
	startNewConversation,
	toggleMaximized,
} from "@/store/assistant";
import type { AssistantOrbConfig } from "@/types/ai-assistant-orb";
import { PV } from "./assistant-panel.tokens";
import { AssistantOrb } from "./orb";
import { ORB_CANVAS_HEADER_PX, ORB_HEADER_SLOT_PX } from "./orb/orb.config";
import { useAssistantText } from "./use-assistant-access";

/**
 * Header panel: nama asisten dari config, badge Beta, ☰ / percakapan baru /
 * perbesar / tutup. Mode tertanam (halaman Bantuan) tanpa perbesar & tutup.
 */
export function AssistantHeader({
	name,
	orb,
	onClose,
}: {
	name: string;
	/** Logo kustom dari status; kosong = ikon bintang bawaan. */
	orb: AssistantOrbConfig;
	/** Tidak diisi = mode tertanam. */
	onClose?: () => void;
}) {
	const { maximized, view } = useSnapshot(assistantStore);
	const text = useAssistantText();

	const icon = (
		label: string,
		onClick: () => void,
		child: React.ReactNode,
		pressed?: boolean,
	) => (
		<Tooltip label={label} withArrow>
			<ActionIcon
				variant={pressed ? "light" : "subtle"}
				color="gray"
				aria-label={label}
				aria-pressed={pressed}
				onClick={onClick}
			>
				{child}
			</ActionIcon>
		</Tooltip>
	);

	return (
		<Group
			justify="space-between"
			wrap="nowrap"
			px="md"
			py="sm"
			style={{
				borderBottom: `1px solid ${PV.divider}`,
				background: PV.header,
			}}
		>
			<Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
				{/* Slot header tetap; kanvas orb dipusatkan SECARA ABSOLUT di dalamnya
				    dan meluber merata ke kedua sisi tanpa menggeser teks. Dengan
				    `display: grid` biasa, trek 24px ikut melebar mengikuti isi 34px
				    dan seluruh luberan jatuh ke kanan, menempel ke nama asisten. */}
				<span
					data-assistant-header-orb-slot
					style={{
						position: "relative",
						width: ORB_HEADER_SLOT_PX,
						height: ORB_HEADER_SLOT_PX,
						flexShrink: 0,
					}}
				>
					<span
						data-assistant-header-orb
						style={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							display: "grid",
						}}
					>
						<AssistantOrb
							config={orb}
							name={name}
							size={ORB_CANVAS_HEADER_PX[orb.size]}
						/>
					</span>
				</span>
				<Text fw={700} truncate>
					{name}
				</Text>
				<Badge size="xs" variant="light">
					{text.beta}
				</Badge>
			</Group>
			<Group gap={4} wrap="nowrap">
				{icon(
					text.conversations,
					() => setAssistantView(view === "list" ? "chat" : "list"),
					<IconMenu2 size={18} />,
					view === "list",
				)}
				{icon(
					text.newConversation,
					startNewConversation,
					<IconMessagePlus size={18} />,
				)}
				{onClose
					? icon(
							maximized ? text.restore : text.maximize,
							toggleMaximized,
							maximized ? (
								<IconArrowsMinimize size={18} />
							) : (
								<IconArrowsMaximize size={18} />
							),
						)
					: null}
				{onClose ? icon(text.close, onClose, <IconX size={18} />) : null}
			</Group>
		</Group>
	);
}
